import Link from "next/link";
import Image from "next/image";
import {
  FocusEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "react-responsive";
import { Drawer } from "@material-tailwind/react";
import { useAuth } from "@/contexts/auth/AuthContext";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import { useSavedResources } from "@/contexts/savedResources/SavedResourcesContext";
import Analytics from "@/lib/analytics";
import {
  handleBulkAddActivitiesToSavedResourcesEvent,
  handleBulkAddSkillsToSavedResourcesEvent,
  handleBulkRemoveFromSavedResourcesEvent,
  SavedResourcesLocation,
} from "@/lib/analytics/savedResources";
import {
  Activity,
  Competency,
  isCompetency,
  isLearnerOutcome,
  LearnerOutcome,
  SubCompetency,
} from "@/lib/models";
import responsive from "@/lib/static/responsive";

import ArrowRight from "../icons/ArrowRight";
import BookmarkIcon from "../icons/Bookmark";
import BookmarkXIcon from "../icons/BookmarkedX";
import BookmarkChecked from "../icons/BookmarkChecked";
import InfoIcon from "../icons/Info";
import XIcon from "../icons/XIcon";

import { HtmlRenderer } from "../HtmlRenderer";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "../StyledButton";

import { OverviewComponentSkillModal } from "./OverviewComponentSkillModal";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "./SavedResourcesToast";
import { ErrorToast } from "./ErrorToast";
import { useNavigator } from "@/contexts/navigator/NavigatorContext";
import { sortBy } from "@/lib/utils";

type CompetencyDrawerProps = {
  entity: Competency | LearnerOutcome | null;
  clearEntity: () => void;
};

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
  currentResource: ResourcePath | null;
};

export const OverviewDrawer = ({
  entity,
  clearEntity,
}: CompetencyDrawerProps) => {
  const [drawerHeight, setDrawerHeight] = useState(554);
  const [drawerHeightClass, setDrawerHeightClass] = useState("!h-[554px]");
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const nodeToRestoreFocus = useRef<EventTarget | null>(null);

  const isLarge = useMediaQuery({
    minWidth: responsive.large,
  });
  const isMedium = useMediaQuery({
    minWidth: responsive.medium,
    maxWidth: responsive.large - 1,
  });
  const isSmall = useMediaQuery({
    minWidth: responsive.small,
    maxWidth: responsive.medium - 1,
  });
  const isXSmall = useMediaQuery({
    maxWidth: responsive.small - 1,
  });

  useEffect(() => {
    switch (true) {
      case !entity:
        setDrawerHeight(0);
        break;
      case isXSmall:
        setDrawerHeight(554);
        setDrawerHeightClass("!h-[554px]");
        break;
      case isSmall:
        setDrawerHeight(576);
        setDrawerHeightClass("!h-[576px]");
        break;
      case isMedium || isLarge:
      default:
        setDrawerHeight(300);
        setDrawerHeightClass("!h-[300px]");
        break;
    }
  }, [isLarge, isMedium, isSmall, isXSmall, entity]);

  useEffect(() => {
    if (!!entity) {
      drawerContentRef?.current?.focus();
    }

    return () => {
      nodeToRestoreFocus.current = null;
    };
  }, [entity]);

  const { getEntityHierarchyFromId } = useEntities();
  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
      currentResource: null,
    });
  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);
  const [componentSkillIdForModal, setComponentSkillIdForModal] =
    useState<string>("");

  const onModalClose = () => {
    setComponentSkillIdForModal((prev) => {
      Analytics.handleDrawerModalClose(getEntityHierarchyFromId(prev));
      return "";
    });
  };

  const handleCloseSavedResourcesToast = useCallback(() => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCloseErrorToast = useCallback(() => {
    setIsErrorToastOpen(false);
  }, []);

  const handleFocus: FocusEventHandler<HTMLDivElement> = (e) => {
    // This conditional is necessary because of possible focus events *inside* the drawer
    // We don't want to overwrite our ref in these instances, only update when the drawer opens/closes.
    if (!nodeToRestoreFocus.current) {
      nodeToRestoreFocus.current = e.relatedTarget;
    }
  };

  const handleDrawerClose = () => {
    clearEntity();
    if (nodeToRestoreFocus.current) {
      (nodeToRestoreFocus.current as HTMLElement).focus();
    }
  };

  return (
    <>
      <Drawer
        placement="bottom"
        open={!!entity}
        ref={drawerContentRef}
        onClose={handleDrawerClose}
        overlay={false}
        className={`z-50 ${drawerHeightClass} !max-h-[calc(100vh-80px)] !max-w-screen-large overflow-y-auto rounded-t-3xl border bg-white shadow-[0_-6px_6px_rgba(10,18,27,0.04)] medium:overflow-y-hidden large:left-[calc((100vw-1440px)/2)]`}
        size={drawerHeight}
        dismiss={{
          outsidePress: false,
        }}
        onFocus={handleFocus}
      >
        {!!entity && (
          <div className="relative h-full">
            <div className="sticky top-0 z-1 h-14 bg-white medium:h-0 medium:border-none">
              <button
                className={`absolute right-4 top-4 rounded-full p-1 hover:bg-black/5 active:bg-black/10 ${
                  isCompetency(entity) ? "medium:hidden" : ""
                }`}
                onClick={clearEntity}
                aria-label="Close Drawer"
              >
                <XIcon className="stroke-2" />
              </button>
            </div>
            {isCompetency(entity) && (
              <CompetencyDrawerContent
                competency={entity}
                clearCompetency={clearEntity}
                openSuccessToast={(currentAction, currentResource) =>
                  setSavedResourcesToastState({
                    isOpen: true,
                    currentAction,
                    currentResource,
                  })
                }
                openErrorToast={() => setIsErrorToastOpen(true)}
                openComponentSkillModal={(id) =>
                  setComponentSkillIdForModal(id)
                }
              />
            )}
            {isLearnerOutcome(entity) && (
              <LearnerOutcomeDrawerContent learnerOutcome={entity} />
            )}
          </div>
        )}
      </Drawer>
      <SavedResourcesToast
        isOpen={savedResourcesToastState.isOpen}
        currentAction={savedResourcesToastState.currentAction}
        resourcePath={savedResourcesToastState.currentResource}
        handleClose={handleCloseSavedResourcesToast}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={handleCloseErrorToast}
      />
      <OverviewComponentSkillModal
        componentSkillId={componentSkillIdForModal}
        onClose={onModalClose}
      />
    </>
  );
};

type CompetencyDrawerContentProps = {
  competency: Competency;
  clearCompetency: () => void;
  openSuccessToast: (
    type: SavedResourcesAction,
    resource: ResourcePath,
  ) => void;
  openErrorToast: () => void;
  openComponentSkillModal: (id: string) => void;
};

const CompetencyDrawerContent = ({
  competency,
  clearCompetency,
  openSuccessToast,
  openErrorToast,
  openComponentSkillModal,
}: CompetencyDrawerContentProps) => {
  const { user, openLoginPrompt } = useAuth();
  const {
    getEntityHierarchyFromId,
    getSubCompetency,
    getActivitiesForCompetency,
    getEntityHierarchyFromActivitySlug,
  } = useEntities();
  const {
    componentSkills: savedComponentSkills,
    addComponentSkill,
    removeComponentSkill,
    activities: savedActivities,
    addActivity,
    removeActivity,
  } = useSavedResources();

  const { areComponentSkillsActive, updateAreComponentSkillsActive } =
    useNavigator();

  const activities = getActivitiesForCompetency(competency.id);

  const savedComponentSkillIds = useMemo(
    () => savedComponentSkills.map((skill) => skill.id),
    [savedComponentSkills],
  );
  const areAllComponentSkillsSaved = useMemo(
    () =>
      competency.subCompetencies.every((subcompetency) =>
        savedComponentSkillIds.includes(subcompetency),
      ),
    [competency, savedComponentSkillIds],
  );

  const handleSaveOrRemoveAllSkills = async () => {
    if (!user) {
      openLoginPrompt("all_component_skills");
      return;
    }

    try {
      if (areAllComponentSkillsSaved) {
        await removeComponentSkill(...competency.subCompetencies);
        const subCompetencyEntities = competency.subCompetencies.map(
          (subCompetency) => getEntityHierarchyFromId(subCompetency),
        );
        handleBulkRemoveFromSavedResourcesEvent(
          subCompetencyEntities,
          SavedResourcesLocation.Drawer,
        );
        openSuccessToast(
          SavedResourcesAction.Remove,
          ResourcePath.ComponentSkill,
        );
      } else {
        const subCompetencyIdsToAdd = competency.subCompetencies.filter(
          (subcompetency) => !savedComponentSkillIds.includes(subcompetency),
        );
        await addComponentSkill(...subCompetencyIdsToAdd);
        const subCompetencyNames = subCompetencyIdsToAdd.map(
          (subCompetencyId) => getSubCompetency(subCompetencyId)!.name,
        );
        handleBulkAddSkillsToSavedResourcesEvent(
          subCompetencyNames,
          SavedResourcesLocation.Drawer,
        );
        openSuccessToast(SavedResourcesAction.Add, ResourcePath.ComponentSkill);
      }
    } catch (e) {
      console.error(e);
      openErrorToast();
    }
  };

  const handleSaveComponentSkill =
    (subCompetency: SubCompetency, isSaved: boolean) =>
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!user) {
        openLoginPrompt("component_skill");
        return;
      }

      try {
        if (isSaved) {
          await removeComponentSkill(subCompetency.id);
          Analytics.handleRemoveFromSavedResourcesEvent(
            getEntityHierarchyFromId(subCompetency.id),
            SavedResourcesLocation.Drawer,
          );
          openSuccessToast(
            SavedResourcesAction.Remove,
            ResourcePath.ComponentSkill,
          );
        } else {
          await addComponentSkill(subCompetency.id);
          Analytics.handleAddToSavedResourcesEvent(
            getEntityHierarchyFromId(subCompetency.id),
            SavedResourcesLocation.Drawer,
          );
          openSuccessToast(
            SavedResourcesAction.Add,
            ResourcePath.ComponentSkill,
          );
        }
      } catch (e) {
        console.error(e);
        openErrorToast();
      }
    };

  const savedActivitySlugs = useMemo(
    () => savedActivities.map((activity) => activity.slug),
    [savedActivities],
  );

  const areAllActivitiesSaved = useMemo(
    () =>
      activities.every((activity) =>
        savedActivitySlugs.includes(activity.urlSlug),
      ),
    [activities, savedActivitySlugs],
  );

  const handleSaveActivity =
    (activity: Activity, isSaved: boolean) => async () => {
      if (!user) {
        openLoginPrompt("activity");
        return;
      }

      try {
        if (isSaved) {
          await removeActivity(activity.urlSlug);
          Analytics.handleRemoveFromSavedResourcesEvent(
            getEntityHierarchyFromActivitySlug(activity.urlSlug),
            SavedResourcesLocation.Drawer,
          );
          openSuccessToast(SavedResourcesAction.Remove, ResourcePath.Activity);
        } else {
          await addActivity(activity.urlSlug);
          Analytics.handleAddToSavedResourcesEvent(
            getEntityHierarchyFromActivitySlug(activity.urlSlug),
            SavedResourcesLocation.Drawer,
          );
          openSuccessToast(SavedResourcesAction.Add, ResourcePath.Activity);
        }
      } catch (e) {
        console.error(e);
        openErrorToast();
      }
    };

  const handleSaveOrRemoveAllActivities = async () => {
    if (!user) {
      openLoginPrompt("all_activities");
      return;
    }

    try {
      if (areAllActivitiesSaved) {
        const activitySlugs = activities.map((activity) => activity.urlSlug);
        await removeActivity(...activitySlugs);
        const activityEntities = activities.map((activity) =>
          getEntityHierarchyFromActivitySlug(activity.urlSlug),
        );
        handleBulkRemoveFromSavedResourcesEvent(
          activityEntities,
          SavedResourcesLocation.Drawer,
        );
        openSuccessToast(SavedResourcesAction.Remove, ResourcePath.Activity);
      } else {
        const activitiesToAdd = activities.filter(
          (activity) => !savedActivitySlugs.includes(activity.urlSlug),
        );
        const activitySlugs = activitiesToAdd.map(
          (activity) => activity.urlSlug,
        );
        await addActivity(...activitySlugs);
        handleBulkAddActivitiesToSavedResourcesEvent(
          activitiesToAdd,
          SavedResourcesLocation.Drawer,
        );
        openSuccessToast(SavedResourcesAction.Add, ResourcePath.Activity);
      }
    } catch (e) {
      console.error(e);
      openErrorToast();
    }
  };

  const handleOpenComponentSkillModal =
    (subCompetency: SubCompetency) => () => {
      openComponentSkillModal(subCompetency.id);
      Analytics.handleDrawerModalOpen(
        getEntityHierarchyFromId(subCompetency.id),
      );
    };

  const learnerOutcomeId = competency?.id.slice(0, 2);

  return (
    <section className="medium:grid medium:grid-cols-2">
      <div className="border-b border-tab-border p-4 small:px-6 small:pb-6 medium:min-h-[298px] medium:border-b-0 medium:border-r medium:pb-8 medium:pl-8 medium:pr-5 medium:pt-8">
        <div className="flex items-center">
          <Image
            src={competency.icon.sourceUrl!}
            width={48}
            height={56}
            alt={competency.name}
            className="mr-3"
          />
          <div className="flex flex-col font-medium">
            <h2 className="font-roobert text-xl leading-normal small:text-[28px]">
              {competency.name}
            </h2>
            <p className="text-base leading-5">{competency.descriptionShort}</p>
          </div>
        </div>
        {!!competency.definitionDescription && (
          <div className="mt-4 line-clamp-3 leading-5 text-gray-text small:line-clamp-4">
            {competency.definitionDescription}
          </div>
        )}
        <Link
          shallow={true}
          href={{
            pathname: "/",
            query: { selection: competency.id },
          }}
          className="mt-6 inline-flex h-10 shrink-0 items-center justify-center gap-1 rounded-full bg-BG-60 py-3.5 pl-4 pr-3 font-semibold text-white hover:bg-BG-70 active:bg-BG-80"
        >
          Learn More
          <ArrowRight fill="#FFFFFF" />
        </Link>
      </div>
      <div className="min-h-[272px] px-4 pb-4 pt-6 small:px-6 small:pb-6 medium:pt-4">
        <div className="mb-4 flex h-8 items-center justify-between">
          <div className="flex gap-4 medium:gap-6">
            <button
              className={`font-roobert text-base font-semibold hover:text-dark-color ${
                areComponentSkillsActive ? "text-dark-color" : "text-grey-50"
              }`}
              onClick={() => updateAreComponentSkillsActive(true)}
            >
              Component Skills ({competency.subCompetencies.length})
            </button>
            <button
              className={`font-roobert text-base font-semibold hover:text-dark-color ${
                !areComponentSkillsActive ? "text-dark-color" : "text-grey-50"
              }`}
              onClick={() => updateAreComponentSkillsActive(false)}
            >
              Activities ({activities.length})
            </button>
          </div>

          <div className="flex">
            {areComponentSkillsActive ? (
              <StyledButton
                buttonType={ButtonTypes.Tertiary}
                size={ButtonSizes.Small}
                iconType={IconTypes.Only}
                onClick={handleSaveOrRemoveAllSkills}
                className="medium:w-auto medium:py-3.5 medium:pl-3 medium:pr-4"
              >
                {areAllComponentSkillsSaved ? (
                  <>
                    <BookmarkXIcon />
                    <span className="hidden text-base font-semibold text-BG-60 medium:inline">
                      Remove all
                    </span>
                  </>
                ) : (
                  <>
                    <BookmarkIcon />
                    <span className="hidden text-base font-semibold text-BG-60 medium:inline">
                      Save all
                    </span>
                  </>
                )}
              </StyledButton>
            ) : (
              <StyledButton
                buttonType={ButtonTypes.Tertiary}
                size={ButtonSizes.Small}
                iconType={IconTypes.Only}
                onClick={handleSaveOrRemoveAllActivities}
                className="medium:w-auto medium:py-3.5 medium:pl-3 medium:pr-4"
              >
                {areAllActivitiesSaved ? (
                  <>
                    <BookmarkXIcon />
                    <span className="hidden text-base font-semibold text-BG-60 medium:inline">
                      Remove all
                    </span>
                  </>
                ) : (
                  <>
                    <BookmarkIcon />
                    <span className="hidden text-base font-semibold text-BG-60 medium:inline">
                      Save all
                    </span>
                  </>
                )}
              </StyledButton>
            )}

            <button
              className="-mr-2 ml-4 hidden rounded-full p-1 hover:bg-black/5 active:bg-black/10 medium:block"
              onClick={clearCompetency}
              aria-label="Close Drawer"
            >
              <XIcon className="stroke-2" />
            </button>
          </div>
        </div>

        {areComponentSkillsActive ? (
          <div
            className="rounded-lg border medium:max-h-[216px] medium:overflow-y-auto"
            key={competency.id}
          >
            {competency.subCompetencies.sort(sortBy).map((subCompetencyId) => {
              const subCompetency = getSubCompetency(subCompetencyId)!;
              const isSaved = savedComponentSkillIds.includes(subCompetencyId);
              return (
                <div
                  key={subCompetency.id}
                  onClick={handleOpenComponentSkillModal(subCompetency)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenComponentSkillModal(subCompetency)();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="flex h-[63px] items-center justify-between border-b-tab-border px-4 py-[15px] text-base font-medium [&:not(:last-child)]:border-b"
                  aria-label={subCompetency.name}
                >
                  <div className="flex items-center justify-center">
                    <div
                      className={`min-h-8 min-w-8 rounded-full bg-${learnerOutcomeId}-30 mr-4 content-center text-center uppercase`}
                    >
                      {subCompetency.id.slice(-1)}
                    </div>
                    <div>{subCompetency.name}</div>
                  </div>
                  <div className="inline-flex gap-2">
                    <button
                      className="flex h-8 w-8 items-center justify-center rounded-full p-1 hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      aria-label="More Info"
                    >
                      <InfoIcon />
                    </button>

                    <button
                      onClick={
                        handleSaveComponentSkill(
                          subCompetency,
                          isSaved,
                        ) as unknown as MouseEventHandler<HTMLButtonElement>
                      }
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-tab-border p-1 hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      aria-label={isSaved ? "Unsave" : "Save"}
                    >
                      {isSaved ? <BookmarkChecked /> : <BookmarkIcon />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {activities.length > 0 ? (
              <ul
                className="rounded-lg border medium:max-h-[216px] medium:overflow-y-auto"
                key={competency.id}
              >
                {activities.map((activity) => {
                  const isSaved = savedActivitySlugs.includes(activity.urlSlug);
                  return (
                    <li
                      key={activity.urlSlug}
                      className="flex h-[63px] items-center justify-between border-b-tab-border p-2 pr-4 [&:not(:last-child)]:border-b"
                      aria-label={activity.name}
                    >
                      <Link
                        className="group flex w-full items-center justify-between"
                        shallow={true}
                        href={{
                          pathname: `/activities/${activity.urlSlug}`,
                        }}
                      >
                        <div className="flex items-center">
                          <Image
                            src={activity.activityCard.sourceUrl!}
                            alt={activity.name}
                            width={85}
                            height={48}
                            className="rounded"
                          />

                          <div className="ml-4 font-semibold leading-tight text-dark-color">
                            {activity.name}
                          </div>
                        </div>

                        <div className="mr-2 flex h-8 w-8 items-center justify-center rounded-full p-1 group-hover:bg-tab-bg-hover group-active:bg-tab-bg-press">
                          <ArrowRight className="shrink-0" />
                        </div>
                      </Link>

                      <div className="inline-flex">
                        <button
                          onClick={handleSaveActivity(activity, isSaved)}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-tab-border p-1 hover:bg-tab-bg-hover active:bg-tab-bg-press"
                          aria-label={isSaved ? "Unsave" : "Save"}
                        >
                          {isSaved ? <BookmarkChecked /> : <BookmarkIcon />}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mt-[72px] flex justify-center text-center text-gray-text">
                Currently no related activities for this competency
              </div>
            )}

            {activities.length < 3 && (
              <div className="absolute bottom-4 left-0 flex w-full justify-center medium:bottom-6 medium:right-6 medium:w-auto medium:justify-end">
                <StyledButton
                  buttonType={ButtonTypes.Tertiary}
                  size={ButtonSizes.Medium}
                  iconType={IconTypes.Right}
                  as="link"
                  href="/activities"
                >
                  Explore all activities
                  <ArrowRight fill="#145758" />
                </StyledButton>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

type LearnerOutcomeDrawerContentProps = {
  learnerOutcome: LearnerOutcome;
};

const LearnerOutcomeDrawerContent = ({
  learnerOutcome,
}: LearnerOutcomeDrawerContentProps) => {
  return (
    <section>
      <div className="border-b border-tab-border px-4 pb-4 pt-[70px] small:px-6 small:pb-6 medium:min-h-[298px] medium:border-b-0 medium:pb-8 medium:pl-8 medium:pr-5 medium:pt-8">
        <div className="flex items-center pb-6">
          <Image
            src={learnerOutcome.learnerOutcomeCard.sourceUrl!}
            width={48}
            height={56}
            alt={learnerOutcome.name}
            className="mr-3.5"
          />
          <h2 className="font-roobert text-xl font-medium leading-normal small:text-[28px] small:font-medium">
            {learnerOutcome.name}
          </h2>
        </div>
        <div className="font-roobert text-base font-normal leading-5 text-gray-text small:mr-20 medium:w-[65%] large:w-[50%]">
          <HtmlRenderer html={learnerOutcome.description} />
        </div>

        <Link
          shallow={true}
          href={{
            pathname: "/",
            query: { selection: learnerOutcome.id },
          }}
          className="mt-6 inline-flex h-10 shrink-0 items-center justify-center gap-1 rounded-full bg-BG-60 py-3.5 pl-4 pr-3 font-semibold text-white hover:bg-BG-70 active:bg-BG-80"
        >
          Learn More
          <ArrowRight fill="#FFFFFF" />
        </Link>
      </div>
    </section>
  );
};
